import SoftBadge from "@/components/atoms/SoftBadge";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import AllOpcosHeader from "@/components/molecules/table-headers/AllOpcosHeader";
import Table from "@/components/molecules/table/Table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import Opco from "@/interfaces/Opco";
import { useDeleteOpcoMutation, useGetOpcosQuery } from "@/services/opcos";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { ElementRef, FC, useRef } from "react";
import { Button } from "react-bootstrap";
import { ToastContentProps, toast } from "react-toastify";

const OpcosView: FC = () => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const [deleteOpcoAsync] = useDeleteOpcoMutation();

  const [state, setState] = useTableState<Partial<TableState>>("opcos", {
    pagination: {
      pageIndex: 0,
      pageSize: 15,
    },
    globalFilter: "",
  });

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data } = useGetOpcosQuery({
    ...state.pagination,
    query: debouncedQuery,
    ...(state.sorting?.[0] && {
      sortBy: state.sorting[0].id,
      sortDirection: state.sorting[0].desc ? "desc" : "asc",
    }),
  });

  const onDelete = async (id: string) => {
    toast.promise(deleteOpcoAsync(id).unwrap(), {
      pending: "Suppression de la catégorie en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p className="mb-0 text-center">L'opco à bien été supprimé 🤩</p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          return "Une erreur est survenue";
        },
      },
    });
  };
  const columns = useRef<ColumnDef<Opco>[]>([
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => (
        <Button
          variant="danger"
          size="sm"
          onClick={() => DeleteModalRef.current?.showOrHiddenModal(id)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "#",
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => id,
      id: "id",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom",
      footer: (props) => props.column.id,
      accessorFn: ({ name, backgroundColor, textColor }) => (
        <SoftBadge bg={backgroundColor} textColor={textColor}>
          {name}
        </SoftBadge>
      ),
      id: "name",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  return (
    <div className="formalys-page formalys-list-page">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimé la catégorie qui a pour id : {{id}} ?"
      />
      <Table
        resource="opcos"
        HeaderComponent={AllOpcosHeader}
        columns={columns}
        state={state}
        tableName="opcos"
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default OpcosView;
